import Phaser from 'phaser';
import {
  PlayerPosition,
  Card as CardType,
  CardDealtMessage,
  StockDrawMessage,
  AnimationEvent,
  AnimationSequenceId,
  GAME_WIDTH,
  GAME_HEIGHT,
  CARD_DEAL_STAGGER_MS,
} from '@hokm/shared';
import { RENDER_SCALE } from '../../../config';
import { PlayerPositionConfig } from '../../../config/positions';

export interface DealingAnimatorCallbacks {
  getPositionConfig: () => Record<PlayerPosition, PlayerPositionConfig>;
  toDisplayPosition: (serverPosition: PlayerPosition) => PlayerPosition;
  getMyPlayerId: () => string | undefined;
  getCardRandomOffset: (cardId: string) => { x: number; y: number; rotation: number };
  sendAnimationAck: (event: AnimationEvent, sequenceId: AnimationSequenceId) => void;
  onCardDealt: (cardId: string, card: CardType, recipientId: string, displayPosition: PlayerPosition) => void;
  updateDeckVisual: (cardsRemaining: number) => void;
  getDealtCardCount: () => number;
  getPlayerCount: () => number;
  isCollectingTrick: () => boolean;
}

/**
 * Handles card dealing and stock draw animations.
 */
export class DealingAnimator {
  private scene: Phaser.Scene;
  private callbacks: DealingAnimatorCallbacks;
  private pendingDealtCards: CardDealtMessage[] = [];
  private isClearing: boolean = false;

  constructor(scene: Phaser.Scene, callbacks: DealingAnimatorCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  setClearing(clearing: boolean): void {
    this.isClearing = clearing;
  }

  getPendingCards(): CardDealtMessage[] {
    return this.pendingDealtCards;
  }

  clearPendingCards(): void {
    this.pendingDealtCards = [];
  }

  /**
   * Animate a card being dealt from the deck.
   */
  animateCardDealt(data: CardDealtMessage): void {
    if (this.callbacks.isCollectingTrick()) return;

    if (this.isClearing) {
      this.pendingDealtCards.push(data);
      return;
    }

    const displayPosition = this.callbacks.toDisplayPosition(data.recipientPosition);
    const targetConfig = this.callbacks.getPositionConfig()[displayPosition];
    const isMe = data.recipientId === this.callbacks.getMyPlayerId();

    // Card starts at deck position
    const startX = GAME_WIDTH / 2;
    const startY = GAME_HEIGHT / 2 - 30;

    const textureKey = isMe ? `card-${data.card.suit}-${data.card.rank}` : 'card-back';
    const cardImg = this.scene.add.image(startX, startY, textureKey);
    cardImg.setScale(0.15 / RENDER_SCALE);
    cardImg.setAlpha(0);
    cardImg.setDepth(40);

    const offset = this.callbacks.getCardRandomOffset(data.card.id);
    let targetX: number, targetY: number, targetRotation: number;

    if (isMe) {
      targetX = targetConfig.animationStart.x + offset.x;
      targetY = targetConfig.animationStart.y - 20 + offset.y;
      targetRotation = offset.rotation;
    } else {
      targetX = targetConfig.cards.x + offset.x;
      targetY = targetConfig.cards.y + offset.y;
      targetRotation = targetConfig.cards.rotation + offset.rotation;
    }

    const staggerDelay = data.cardIndexInBatch * CARD_DEAL_STAGGER_MS;

    this.scene.tweens.add({
      targets: cardImg,
      x: targetX,
      y: targetY,
      rotation: targetRotation,
      scale: (isMe ? 0.5 : 0.4) / RENDER_SCALE,
      alpha: 1,
      delay: staggerDelay,
      duration: 300,
      ease: 'Cubic.easeOut',
      onStart: () => {
        // Track card when animation starts (after stagger delay)
        // This makes cards appear in hand as they're visually dealt
        this.callbacks.onCardDealt(data.card.id, data.card, data.recipientId, displayPosition);
      },
      onComplete: () => {
        if (data.isLastCardInRound) {
          this.callbacks.sendAnimationAck(AnimationEvent.DEAL_BATCH, data.batchSequenceId);
        }

        this.scene.time.delayedCall(50, () => {
          this.scene.tweens.add({
            targets: cardImg,
            alpha: 0,
            duration: 100,
            onComplete: () => cardImg.destroy(),
          });
        });
      },
    });

    // Update deck visual
    const totalPlayers = this.callbacks.getPlayerCount();
    const cardsDealtSoFar = this.callbacks.getDealtCardCount();
    const estimatedRemaining = 52 - cardsDealtSoFar * totalPlayers;
    this.callbacks.updateDeckVisual(Math.max(0, estimatedRemaining));
  }

  /**
   * Animate a card being drawn from stock (2-player mode).
   */
  animateStockDraw(data: StockDrawMessage): void {
    if (this.callbacks.isCollectingTrick()) return;

    const displayPosition = this.callbacks.toDisplayPosition(data.recipientPosition);
    const targetConfig = this.callbacks.getPositionConfig()[displayPosition];
    const isMe = data.recipientId === this.callbacks.getMyPlayerId();

    // Track card immediately
    this.callbacks.onCardDealt(data.card.id, data.card, data.recipientId, displayPosition);

    // Card starts at deck position
    const startX = GAME_WIDTH / 2;
    const startY = GAME_HEIGHT / 2 - 30;

    const textureKey = isMe ? `card-${data.card.suit}-${data.card.rank}` : 'card-back';
    const cardImg = this.scene.add.image(startX, startY, textureKey);
    cardImg.setScale(0.3 / RENDER_SCALE);
    cardImg.setAlpha(0);
    cardImg.setDepth(40);

    const offset = this.callbacks.getCardRandomOffset(data.card.id);
    let targetX: number, targetY: number, targetRotation: number;

    if (isMe) {
      targetX = targetConfig.animationStart.x + offset.x;
      targetY = targetConfig.animationStart.y - 20 + offset.y;
      targetRotation = offset.rotation;
    } else {
      targetX = targetConfig.cards.x + offset.x;
      targetY = targetConfig.cards.y + offset.y;
      targetRotation = targetConfig.cards.rotation + offset.rotation;
    }

    this.scene.tweens.add({
      targets: cardImg,
      x: targetX,
      y: targetY,
      rotation: targetRotation,
      scale: (isMe ? 0.5 : 0.4) / RENDER_SCALE,
      alpha: 1,
      duration: 250,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        this.scene.time.delayedCall(30, () => {
          this.scene.tweens.add({
            targets: cardImg,
            alpha: 0,
            duration: 80,
            onComplete: () => cardImg.destroy(),
          });
        });
      },
    });

    // Update deck visual
    this.callbacks.updateDeckVisual(data.stockRemaining);
  }

  /**
   * Process any queued dealt cards.
   */
  async processPendingCards(): Promise<void> {
    for (const data of this.pendingDealtCards) {
      this.animateCardDealt(data);
      await new Promise<void>((resolve) => this.scene.time.delayedCall(100, resolve));
    }
    this.pendingDealtCards = [];
  }

  destroy(): void {
    this.pendingDealtCards = [];
  }
}
